import { useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import {
  consoleApi,
  type ProviderSettlementResponse,
  type SettlementPaymentMethod,
  type SettlementStatementRecord,
} from "../../../lib/admin-console-api";
import { useAppPreferences } from "../../../lib/preferences";
import { baseLabel } from "../activity";
import {
  currentMonth,
  errorMessage,
  Facts,
  LoadState,
  monthBounds,
  PageHeader,
  Section,
  StatusChip,
  useFormatters,
  useLoad,
  useNotices,
  useReasonPrompt,
} from "../ui";

function MonthPicker({ month, onChange }: { month: string; onChange: (value: string) => void }) {
  const { t } = useAppPreferences();
  return (
    <label className="ops-field console-month">
      <span className="ops-label">{t("Month")}</span>
      <input className="ops-input" max={currentMonth()} onChange={(event) => event.target.value && onChange(event.target.value)} type="month" value={month} />
    </label>
  );
}

export function SettlementsPage() {
  const { t } = useAppPreferences();
  const { money } = useFormatters();
  const [params, setParams] = useSearchParams();
  const month = params.get("month") ?? currentMonth(-1);
  const overview = useLoad(() => consoleApi.settlementOverview(monthBounds(month)), [month]);
  const rows = overview.data?.rows ?? [];

  return (
    <div className="console-page">
      <PageHeader
        actions={<MonthPicker month={month} onChange={(value) => setParams({ month: value })} />}
        description={t("Players pay venues directly. Each provider owes gaamed its commission, which you settle here.")}
        kicker={t("Platform")}
        title={t("Settlements")}
      />
      <LoadState error={overview.error} isEmpty={!overview.data} loading={overview.loading} onRetry={() => void overview.reload()}>
        {overview.data ? (
          <>
            <div className="console-card-grid">
              <div className="ops-stat-card">
                <p className="ops-stat-label">{t("Commission for this month")}</p>
                <p className="ops-stat-value">{money(overview.data.totals.period_commission_amount)}</p>
              </div>
              <div className="ops-stat-card">
                <p className="ops-stat-label">{t("Owed to gaamed (issued, unpaid)")}</p>
                <p className="ops-stat-value">{money(overview.data.totals.balance_amount)}</p>
              </div>
              <div className="ops-stat-card">
                <p className="ops-stat-label">{t("Overdue")}</p>
                <p className="ops-stat-value">{money(overview.data.totals.overdue_amount)}</p>
              </div>
            </div>
            <LoadState empty={t("No approved providers yet.")} error={null} isEmpty={rows.length === 0} loading={false} onRetry={() => undefined}>
              <div className="ops-surface console-table-wrap">
                <table className="console-table">
                  <thead>
                    <tr>
                      <th scope="col">{t("Provider")}</th>
                      <th scope="col">{t("This month")}</th>
                      <th scope="col">{t("Statement")}</th>
                      <th scope="col">{t("Balance")}</th>
                      <th scope="col">{t("Overdue")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.provider_id}>
                        <td>
                          <Link className="console-link" to={`/admin/settlements/${row.provider_id}?month=${month}`}>{row.provider_name}</Link>
                          {row.venues_without_rules > 0 ? (
                            <span className="console-warning block">{t("{count} live venues without terms", { count: row.venues_without_rules })}</span>
                          ) : null}
                        </td>
                        <td>{money(row.period_commission_amount)}</td>
                        <td>{row.period_statement_id ? <StatusChip status="issued" /> : <span className="console-muted">{t("Not issued")}</span>}</td>
                        <td>{money(row.balance.balance_amount)}</td>
                        <td>{Number(row.balance.overdue_amount) > 0 ? <strong className="console-danger-text">{money(row.balance.overdue_amount)}</strong> : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </LoadState>
          </>
        ) : null}
      </LoadState>
    </div>
  );
}

const METHODS: Array<{ value: SettlementPaymentMethod; label: string }> = [
  { value: "transfer", label: "Bank transfer" },
  { value: "instapay", label: "InstaPay" },
  { value: "wallet", label: "Mobile wallet" },
  { value: "cash", label: "Cash" },
  { value: "other", label: "Other" },
];

export function ProviderSettlementPage() {
  const { providerId = "" } = useParams();
  const { t } = useAppPreferences();
  const { dateOnly, dateTime, money } = useFormatters();
  const { banners, setError, setNotice } = useNotices();
  const { ask, dialog } = useReasonPrompt();
  const [params, setParams] = useSearchParams();
  const month = params.get("month") ?? currentMonth(-1);
  const period = monthBounds(month);
  const settlement = useLoad(() => consoleApi.providerSettlement(providerId, period), [providerId, month]);
  const [busy, setBusy] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [payment, setPayment] = useState({ amount: "", paid_on: new Date().toISOString().slice(0, 10), method: "transfer" as SettlementPaymentMethod, reference: "", note: "" });
  const data = settlement.data;
  const periodEnded = period.period_end <= new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);

  const apply = async (work: () => Promise<ProviderSettlementResponse>, success: string, failure: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      settlement.setData(await work());
      setNotice(success);
    } catch (error) {
      setError(errorMessage(error, failure));
    } finally {
      setBusy(false);
    }
  };

  const issue = () =>
    apply(
      () => consoleApi.issueStatement(providerId, { ...period, due_date: dueDate || null }),
      t("Statement issued. Its amounts are now fixed."),
      t("The statement was not issued."),
    );

  const voidStatement = async (statement: SettlementStatementRecord) => {
    const reason = await ask({
      title: t("Void this statement?"),
      description: t("It stops counting towards the balance. Payments recorded against it stay on the ledger."),
      confirmLabel: t("Void statement"),
      fieldLabel: t("Reason"),
      minLength: 5,
      tone: "danger",
    });
    if (reason) await apply(() => consoleApi.voidStatement(statement.id, reason), t("Statement voided."), t("The statement was not voided."));
  };

  const recordPayment = (event: FormEvent) => {
    event.preventDefault();
    void apply(
      async () => {
        const result = await consoleApi.recordPayment(
          providerId,
          {
            amount: Number(payment.amount),
            paid_on: payment.paid_on,
            method: payment.method,
            reference: payment.reference.trim() || null,
            note: payment.note.trim() || null,
            statement_id: data?.period_statement?.id ?? null,
          },
          period,
        );
        setPayment((current) => ({ ...current, amount: "", reference: "", note: "" }));
        return result;
      },
      t("Payment recorded."),
      t("The payment was not recorded."),
    );
  };

  return (
    <div className="console-page">
      {dialog}
      <PageHeader
        actions={<MonthPicker month={month} onChange={(value) => setParams({ month: value })} />}
        kicker={<Link className="console-link" to={`/admin/settlements?month=${month}`}>{t("Settlements")}</Link>}
        title={data ? data.provider_name : t("Settlement")}
        description={data ? <Link className="console-link" to={`/admin/providers/${data.provider_id}`}>{t("Open provider record")}</Link> : null}
      />
      {banners}
      <LoadState error={settlement.error} isEmpty={!data} loading={settlement.loading} onRetry={() => void settlement.reload()}>
        {data ? (
          <>
            <div className="console-card-grid">
              <div className="ops-stat-card">
                <p className="ops-stat-label">{t("Balance owed to gaamed")}</p>
                <p className="ops-stat-value">{money(data.balance.balance_amount)}</p>
              </div>
              <div className="ops-stat-card">
                <p className="ops-stat-label">{t("Overdue")}</p>
                <p className="ops-stat-value">{money(data.balance.overdue_amount)}</p>
              </div>
              <div className="ops-stat-card">
                <p className="ops-stat-label">{t("Paid to date")}</p>
                <p className="ops-stat-value">{money(data.balance.paid_amount)}</p>
              </div>
            </div>

            <Section
              description={
                data.period_statement
                  ? t("Issued {date}. These figures are fixed; later changes don't affect them.", { date: dateTime(data.period_statement.issued_at) })
                  : t("Live preview from completed sessions. Issue a statement once the month is over to fix the amount.")
              }
              title={t("{start} to {end}", { start: dateOnly(period.period_start), end: dateOnly(new Date(Date.parse(period.period_end) - 86_400_000).toISOString()) })}
            >
              <Breakdown computation={data.period_statement?.breakdown ?? data.preview} />
              {data.venues_without_rules.length > 0 ? (
                <p className="console-warning">{t("No commission terms at: {venues}. Set them on each venue's page.", { venues: data.venues_without_rules.join(", ") })}</p>
              ) : null}
              {data.period_statement ? (
                <div className="console-button-row">
                  <StatusChip status="issued" />
                  <span className="console-muted">
                    {t("Paid {paid} of {total}", { paid: money(data.period_statement.paid_amount), total: money(data.period_statement.commission_amount) })}
                    {data.period_statement.due_date ? ` · ${t("due {date}", { date: dateOnly(data.period_statement.due_date) })}` : ""}
                  </span>
                  <button className="ops-button" data-size="sm" data-variant="secondary" disabled={busy} onClick={() => void voidStatement(data.period_statement!)} type="button">{t("Void")}</button>
                </div>
              ) : periodEnded ? (
                <div className="console-button-row">
                  <label className="ops-field">
                    <span className="ops-label">{t("Due date (optional)")}</span>
                    <input className="ops-input" min={period.period_end} onChange={(event) => setDueDate(event.target.value)} type="date" value={dueDate} />
                  </label>
                  <button className="ops-button" data-variant="primary" disabled={busy} onClick={() => void issue()} type="button">
                    {t("Issue statement for {amount}", { amount: money(data.preview.commission_amount) })}
                  </button>
                </div>
              ) : (
                <p className="console-muted">{t("You can issue this statement after the month ends.")}</p>
              )}
            </Section>

            <div className="console-record-grid">
              <div className="console-record-main">
                <Section title={t("Statements")}>
                  {data.statements.length === 0 ? (
                    <p className="console-muted">{t("No statements issued yet.")}</p>
                  ) : (
                    <div className="console-table-wrap">
                      <table className="console-table">
                        <thead>
                          <tr>
                            <th scope="col">{t("Period")}</th>
                            <th scope="col">{t("Commission")}</th>
                            <th scope="col">{t("Paid")}</th>
                            <th scope="col">{t("Due")}</th>
                            <th scope="col">{t("Status")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.statements.map((statement) => (
                            <tr key={statement.id}>
                              <td>
                                <Link className="console-link" to={`/admin/settlements/${providerId}?month=${statement.period_start.slice(0, 7)}`}>
                                  {dateOnly(statement.period_start)}
                                </Link>
                              </td>
                              <td>{money(statement.commission_amount)}</td>
                              <td>{money(statement.paid_amount)}</td>
                              <td>{statement.due_date ? dateOnly(statement.due_date) : "—"}</td>
                              <td>
                                <StatusChip status={statement.status} />
                                {statement.void_reason ? <span className="console-muted block">{statement.void_reason}</span> : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Section>
                <Section title={t("Payments received")}>
                  {data.payments.length === 0 ? (
                    <p className="console-muted">{t("No payments recorded yet.")}</p>
                  ) : (
                    <ul className="console-list">
                      {data.payments.map((item) => (
                        <li className="console-list-row" key={item.id}>
                          <span className="min-w-0 flex-1">
                            <span className="console-list-title">{money(item.amount)} · {t(METHODS.find((method) => method.value === item.method)?.label ?? item.method)}</span>
                            <span className="console-muted">
                              {dateOnly(item.paid_on)}{item.reference ? ` · ${item.reference}` : ""}{item.recorded_by_name ? ` · ${item.recorded_by_name}` : ""}
                            </span>
                            {item.note ? <span className="console-muted">{item.note}</span> : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>
              </div>
              <div className="console-record-side">
                <Section description={t("Record money the provider sent to gaamed.")} title={t("Record a payment")}>
                  <form className="console-form" onSubmit={recordPayment}>
                    <label className="ops-field">
                      <span className="ops-label">{t("Amount (EGP)")}</span>
                      <input className="ops-input" inputMode="decimal" min={0.01} onChange={(event) => setPayment((current) => ({ ...current, amount: event.target.value }))} required step={0.01} type="number" value={payment.amount} />
                    </label>
                    <label className="ops-field">
                      <span className="ops-label">{t("Paid on")}</span>
                      <input className="ops-input" max={new Date().toISOString().slice(0, 10)} onChange={(event) => setPayment((current) => ({ ...current, paid_on: event.target.value }))} required type="date" value={payment.paid_on} />
                    </label>
                    <label className="ops-field">
                      <span className="ops-label">{t("Method")}</span>
                      <select className="ops-select" onChange={(event) => setPayment((current) => ({ ...current, method: event.target.value as SettlementPaymentMethod }))} value={payment.method}>
                        {METHODS.map((method) => <option key={method.value} value={method.value}>{t(method.label)}</option>)}
                      </select>
                    </label>
                    <label className="ops-field">
                      <span className="ops-label">{t("Reference (optional)")}</span>
                      <input className="ops-input" maxLength={120} onChange={(event) => setPayment((current) => ({ ...current, reference: event.target.value }))} value={payment.reference} />
                    </label>
                    <label className="ops-field console-form-wide">
                      <span className="ops-label">{t("Note (optional)")}</span>
                      <input className="ops-input" maxLength={500} onChange={(event) => setPayment((current) => ({ ...current, note: event.target.value }))} value={payment.note} />
                    </label>
                    <p className="console-muted console-form-wide">
                      {data.period_statement ? t("Linked to this month's statement.") : t("Counts towards the overall balance.")}
                    </p>
                    <div className="console-form-wide">
                      <button className="ops-button" data-variant="primary" disabled={busy || !payment.amount} type="submit">{t("Record payment")}</button>
                    </div>
                  </form>
                </Section>
              </div>
            </div>
          </>
        ) : null}
      </LoadState>
    </div>
  );
}

function Breakdown({ computation }: { computation: ProviderSettlementResponse["preview"] }) {
  const { t } = useAppPreferences();
  const { money } = useFormatters();
  return (
    <>
      <Facts
        items={[
          [t("Session time charged by venues"), money(computation.session_time_amount)],
          [t("Food and drinks charged by venues"), money(computation.food_amount)],
          [t("Commission"), <strong key="commission">{money(computation.commission_amount)}</strong>],
        ]}
      />
      <div className="console-table-wrap">
        <table className="console-table">
          <thead>
            <tr>
              <th scope="col">{t("Venue")}</th>
              <th scope="col">{t("Sessions")}</th>
              <th scope="col">{t("Rule")}</th>
              <th scope="col">{t("Charged on")}</th>
              <th scope="col">{t("Commission")}</th>
            </tr>
          </thead>
          <tbody>
            {computation.venues.flatMap((venue) => {
              if (venue.rules.length === 0) {
                return [
                  <tr key={venue.venue_id}>
                    <td><Link className="console-link" to={`/admin/venues/${venue.venue_id}`}>{venue.venue_name}</Link></td>
                    <td>{venue.session_count}</td>
                    <td className="console-muted">{venue.has_rules ? t("No rule covered these dates") : t("No terms set")}</td>
                    <td>—</td>
                    <td>{money(0)}</td>
                  </tr>,
                ];
              }
              return venue.rules.map((rule, index) => (
                <tr key={`${venue.venue_id}-${rule.rule_id}`}>
                  <td>{index === 0 ? <Link className="console-link" to={`/admin/venues/${venue.venue_id}`}>{venue.venue_name}</Link> : null}</td>
                  <td>{rule.session_count}</td>
                  <td>{t("{percent}% of {base}", { percent: rule.percent, base: baseLabel(rule.base, t) })}</td>
                  <td>{money(rule.base_amount)}</td>
                  <td>{money(rule.commission_amount)}</td>
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>
      {computation.venues.some((venue) => venue.sessions_without_rule > 0) ? (
        <p className="console-warning">
          {t("{count} sessions had no matching rule and earned no commission.", {
            count: computation.venues.reduce((sum, venue) => sum + venue.sessions_without_rule, 0),
          })}
        </p>
      ) : null}
    </>
  );
}
